if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

        // Dawn spouští video až při přepnutí slidu, takže první slide zůstal stát na
        // posteru s bílým play tlačítkem. Origin therabody.eu video přehrává rovnou,
        // tak ho pro úvodní aktivní slide načteme hned. Video je muted, jinak by
        // prohlížeč autoplay odmítl.
        // Počkáme na DOM ready, jinak nemusí být <deferred-media> ještě upgradnutá
        // na custom element a loadContent by neexistoval.
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => this.autoplayInitialMedia(), { once: true });
        } else {
          this.autoplayInitialMedia();
        }

        this.bindVideoToggle();
        this.preloadSlides();
        this.trackMediaLoading();
      }

      // Skeleton je v CSS zapnutý defaultně a zháší se až tady, aby se ukázal
      // i v okamžiku, kdy JS ještě neběží — o to jde. `.tb-media-loaded` zároveň
      // zastaví animaci, ať neběží donekonečna pod hotovým obrázkem.
      trackMediaLoading() {
        const done = (element) => element && element.classList.add('tb-media-loaded');

        this.querySelectorAll('img').forEach((image) => {
          const target = image.closest('.thumbnail') || image.closest('.product-media-container');
          if (!target) return;
          if (image.complete && image.naturalWidth > 0) {
            done(target);
          } else {
            image.addEventListener('load', () => done(target), { once: true });
            // I při chybě skeleton zhasneme — nekonečný shimmer je horší než prázdno.
            image.addEventListener('error', () => done(target), { once: true });
          }
        });

        this.querySelectorAll('video').forEach((video) => {
          const target = video.closest('.product-media-container');
          if (!target) return;
          if (video.readyState >= 2) done(target);
          else video.addEventListener('loadeddata', () => done(target), { once: true });
        });
      }

      // Slidy galerie jsou schované, takže je lazy loading nenačte, dokud na ně
      // uživatel neklikne — a teprve tam začne stahování, což je ta prodleva.
      // Po dokončení načtení stránky je proto přepneme na eager a necháme dojet
      // na pozadí. `fetchPriority: low`, ať to nesoupeří s ničím důležitým.
      preloadSlides() {
        if (!this.elements.viewer) return;

        const connection = navigator.connection;
        if (connection && (connection.saveData || ['slow-2g', '2g'].includes(connection.effectiveType))) return;

        const images = Array.from(this.elements.viewer.querySelectorAll('img[loading="lazy"]'));
        if (!images.length) return;

        // Přednačtení přes odpojený Image() — nastavení srcset+sizes spustí stažení
        // té varianty, kterou by prohlížeč vybral i pro skutečný <img>, takže se
        // naplní HTTP cache a přepnutí slidu už je z cache.
        // Samotné přepnutí `loading` na eager je nespolehlivé: záleží na tom, jestli
        // prohlížeč u už naparsovaného obrázku na změnu atributu zareaguje.
        const warm = () => {
          images.forEach((image) => {
            const preloader = new Image();
            preloader.fetchPriority = 'low';
            if (image.sizes) preloader.sizes = image.sizes;
            if (image.srcset) preloader.srcset = image.srcset;
            else if (image.src) preloader.src = image.src;
            image.loading = 'eager';
          });
        };

        const whenIdle = () => {
          if (typeof window.requestIdleCallback === 'function') {
            window.requestIdleCallback(warm, { timeout: 2000 });
          } else {
            window.setTimeout(warm, 500);
          }
        };

        if (document.readyState === 'complete') {
          whenIdle();
        } else {
          window.addEventListener('load', whenIdle, { once: true });
        }
      }

      // <video> bez `controls` není fokusovatelné, takže by se ke keydown nikdy nedostal.
      // Voláme po každém loadContent(), protože video vzniká až tam.
      prepareVideos() {
        this.querySelectorAll('video:not([data-tb-toggle-ready])').forEach((video) => {
          video.setAttribute('data-tb-toggle-ready', '');
          video.setAttribute('tabindex', '0');
          video.muted = true;
          video.removeAttribute('controls');
          video.style.cursor = 'pointer';
        });
      }

      // Video běží bez nativní ovládací lišty, takže pauzu musíme obsloužit sami.
      // Delegujeme na galerii — video element vzniká až v DeferredMedia.loadContent().
      bindVideoToggle() {
        const toggle = (video) => {
          if (video.paused) {
            video.play();
          } else {
            video.pause();
          }
        };

        this.addEventListener('click', (event) => {
          const video = event.target.closest('video');
          if (video) toggle(video);
        });

        // Bez klávesové obsluhy by autoplay video nešlo zastavit jinak než myší
        // (WCAG 2.2.2 Pause, Stop, Hide).
        this.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
          const video = event.target.closest('video');
          if (!video) return;
          event.preventDefault();
          toggle(video);
        });
      }

      autoplayInitialMedia() {
        const activeItem =
          this.elements.viewer.querySelector('[data-media-id].is-active') ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeItem) return;
        this.loadDeferredIn(activeItem);
        this.prepareVideos();
        this.playVideoIn(activeItem);
      }

      onSlideChanged(event) {
        const current = event.detail.currentElement;
        const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${current.dataset.mediaId}"]`);
        this.setActiveThumbnail(thumbnail);

        // Swipe na mobilu nejde přes setActiveMedia(), takže video rozjedeme i tady.
        // Pauzujeme jen videa uvnitř téhle galerie, ne přes celou stránku jako
        // pauseAllMedia() — jinak by swipe zastavil i pozadí ostatních sekcí.
        this.querySelectorAll('video').forEach((video) => {
          if (!current.contains(video)) video.pause();
        });
        this.playVideoIn(current);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        this.loadDeferredIn(activeItem);
        this.prepareVideos();
        this.playVideoIn(activeItem);
      }

      // loadContent() bezpodmínečně sahá na <template>. Nahrané video renderujeme rovnou
      // do HTML, takže žádný template nemá — bez téhle pojistky by to hodilo TypeError,
      // ten by vybublal ze setActiveMedia() a shodil i přepnutí aktivního thumbnailu.
      loadDeferredIn(container) {
        const deferredMedia = container.querySelector('.deferred-media');
        if (!deferredMedia || typeof deferredMedia.loadContent !== 'function') return;
        if (!deferredMedia.querySelector('template')) return;
        deferredMedia.loadContent(false);
      }

      // Návrat na video slide ho má znovu rozjet, i když ho uživatel předtím pauznul
      // (a `pauseAllMedia()` výš ho stejně zastavilo).
      playVideoIn(container) {
        const video = container.querySelector('video');
        if (!video) return;
        video.muted = true;
        const played = video.play();
        if (played && typeof played.catch === 'function') played.catch(() => {});
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}

